import { publicUrl } from "./publicUrl";

type SoundName = "laser" | "pop" | "splash" | "upgrade" | "win" | "lose" | "ui" | "capture" | "tick" | "ready";

type SampleLayer = {
  src: string;
  volume: number;
  delay?: number;
};

const SAMPLE_LAYERS: Partial<Record<SoundName, SampleLayer[]>> = {
  laser: [{ src: publicUrl("audio/laser-swoosh-mixkit-1467.wav"), volume: 0.24 }],
  pop: [
    { src: publicUrl("audio/balloon-pop-mixkit-2364.wav"), volume: 0.56 },
    { src: publicUrl("audio/water-splash-mixkit-1311.wav"), volume: 0.36, delay: 12 },
  ],
  splash: [
    { src: publicUrl("audio/big-water-hit-mixkit-1180.wav"), volume: 0.72 },
    { src: publicUrl("audio/crowd-aww-tim-kahn-336998.mp3"), volume: 0.58, delay: 90 },
  ],
};

class ArcadeAudio {
  private context: AudioContext | null = null;
  private muted = false;
  private samples = new Map<string, HTMLAudioElement>();
  private active = new Set<HTMLAudioElement>();
  private activeWhistles = new Set<() => void>();
  private music: HTMLAudioElement | null = null;
  private trackingBuzzStop: (() => void) | null = null;

  setMuted(muted: boolean) {
    this.muted = muted;
    if (this.music) this.music.muted = muted;
    if (muted) {
      this.active.forEach((audio) => {
        audio.pause();
        audio.currentTime = 0;
      });
      this.active.clear();
      this.activeWhistles.forEach((stop) => stop());
      this.activeWhistles.clear();
      this.stopTrackingBuzz();
    }
  }

  startTrackingBuzz() {
    if (this.muted || this.trackingBuzzStop) return;
    this.unlock();
    const context = this.context;
    if (!context) return;

    const now = context.currentTime;
    const carrier = context.createOscillator();
    const tremolo = context.createOscillator();
    const tremoloDepth = context.createGain();
    const gain = context.createGain();
    let stopped = false;

    carrier.type = "triangle";
    carrier.frequency.setValueAtTime(118, now);
    tremolo.type = "sine";
    tremolo.frequency.setValueAtTime(2.1, now);
    tremoloDepth.gain.setValueAtTime(0.0045, now);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.009, now + 0.22);

    tremolo.connect(tremoloDepth).connect(gain.gain);
    carrier.connect(gain).connect(context.destination);
    carrier.start(now);
    tremolo.start(now);

    this.trackingBuzzStop = () => {
      if (stopped) return;
      stopped = true;
      const at = context.currentTime;
      gain.gain.cancelScheduledValues(at);
      gain.gain.setTargetAtTime(0.0001, at, 0.035);
      carrier.stop(at + 0.16);
      tremolo.stop(at + 0.16);
      this.trackingBuzzStop = null;
    };
  }

  stopTrackingBuzz() {
    this.trackingBuzzStop?.();
  }

  playTrackingLock() {
    if (this.muted) return;
    this.unlock();
    const context = this.context;
    if (!context) return;
    const now = context.currentTime;

    [0, 0.105].forEach((delay, index) => {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      const at = now + delay;
      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(index === 0 ? 523.25 : 783.99, at);
      gain.gain.setValueAtTime(0.0001, at);
      gain.gain.exponentialRampToValueAtTime(0.045, at + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.14);
      oscillator.connect(gain).connect(context.destination);
      oscillator.start(at);
      oscillator.stop(at + 0.15);
    });
  }

  playIncomingWhistle(durationSeconds: number): (() => void) | null {
    if (this.muted) return null;
    this.unlock();
    const context = this.context;
    if (!context) return null;

    const duration = Math.max(0.45, Math.min(6, durationSeconds));
    const now = context.currentTime;
    const oscillator = context.createOscillator();
    const vibrato = context.createOscillator();
    const vibratoDepth = context.createGain();
    const gain = context.createGain();
    let stopped = false;

    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(1180, now);
    oscillator.frequency.exponentialRampToValueAtTime(245, now + duration);
    vibrato.type = "sine";
    vibrato.frequency.setValueAtTime(5.4, now);
    vibratoDepth.gain.setValueAtTime(34, now);
    vibrato.connect(vibratoDepth).connect(oscillator.frequency);

    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.012, now + Math.min(0.16, duration * 0.12));
    gain.gain.exponentialRampToValueAtTime(0.026, now + duration * 0.82);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    oscillator.connect(gain).connect(context.destination);

    const stop = () => {
      if (stopped) return;
      stopped = true;
      const at = context.currentTime;
      gain.gain.cancelScheduledValues(at);
      gain.gain.setTargetAtTime(0.0001, at, 0.025);
      oscillator.stop(at + 0.12);
      vibrato.stop(at + 0.12);
      this.activeWhistles.delete(stop);
    };
    this.activeWhistles.add(stop);
    oscillator.addEventListener("ended", () => {
      stopped = true;
      this.activeWhistles.delete(stop);
    }, { once: true });
    oscillator.start(now);
    vibrato.start(now);
    oscillator.stop(now + duration + 0.02);
    vibrato.stop(now + duration + 0.02);
    return stop;
  }

  playMusic(src: string) {
    if (typeof Audio === "undefined") return;
    if (this.music?.src.endsWith(src)) {
      this.music.muted = this.muted;
      void this.music.play().catch(() => undefined);
      return;
    }
    this.stopMusic();
    const music = new Audio(src);
    music.loop = true;
    music.volume = 0.2;
    music.muted = this.muted;
    music.preload = "auto";
    this.music = music;
    void music.play().catch(() => undefined);
  }

  stopMusic() {
    if (!this.music) return;
    this.music.pause();
    this.music.currentTime = 0;
    this.music = null;
  }

  unlock() {
    if (!this.context) this.context = new AudioContext();
    void this.context.resume();
    if (typeof Audio === "undefined") return;
    Object.values(SAMPLE_LAYERS).flat().forEach((layer) => {
      if (this.samples.has(layer.src)) return;
      const audio = new Audio(layer.src);
      audio.preload = "auto";
      this.samples.set(layer.src, audio);
      audio.load();
    });
  }

  play(name: SoundName) {
    if (this.muted) return;
    this.unlock();
    const layers = SAMPLE_LAYERS[name];
    if (layers?.length && typeof Audio !== "undefined") {
      layers.forEach((layer) => {
        window.setTimeout(() => this.playSample(layer), layer.delay ?? 0);
      });
      return;
    }
    this.playSynth(name);
  }

  private playSample(layer: SampleLayer) {
    if (this.muted) return;
    const source = this.samples.get(layer.src);
    if (!source) return;
    const audio = source.cloneNode(true) as HTMLAudioElement;
    audio.volume = layer.volume;
    this.active.add(audio);
    const cleanup = () => this.active.delete(audio);
    audio.addEventListener("ended", cleanup, { once: true });
    audio.addEventListener("error", cleanup, { once: true });
    void audio.play().catch(cleanup);
  }

  private playSynth(name: SoundName) {
    const context = this.context;
    if (!context) return;

    const oscillator = context.createOscillator();
    const gain = context.createGain();
    const now = context.currentTime;
    const settings = {
      laser: [720, 180, 0.11, "sawtooth"],
      pop: [260, 80, 0.09, "square"],
      splash: [120, 48, 0.24, "sawtooth"],
      upgrade: [440, 880, 0.32, "triangle"],
      win: [520, 1040, 0.55, "triangle"],
      lose: [220, 70, 0.55, "sawtooth"],
      ui: [420, 560, 0.08, "sine"],
      capture: [360, 720, 0.18, "triangle"],
      tick: [310, 250, 0.08, "square"],
      ready: [480, 960, 0.28, "triangle"],
    }[name] as [number, number, number, OscillatorType];

    oscillator.type = settings[3];
    oscillator.frequency.setValueAtTime(settings[0], now);
    oscillator.frequency.exponentialRampToValueAtTime(settings[1], now + settings[2]);
    gain.gain.setValueAtTime(name === "splash" ? 0.1 : 0.055, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + settings[2]);
    oscillator.connect(gain).connect(context.destination);
    oscillator.start(now);
    oscillator.stop(now + settings[2]);
  }

  announce(text: string) {
    if (this.muted || !("speechSynthesis" in window)) return;
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 1.12;
    utterance.pitch = 1.35;
    utterance.volume = 0.45;
    window.speechSynthesis.speak(utterance);
  }
}

export const arcadeAudio = new ArcadeAudio();
